import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Starfield from "@/components/Starfield";
import Rocket from "@/components/Rocket";
import ClockDock from "@/components/ClockDock";
import {
  fetchClasses,
  createClass,
  deleteClass,
  updateClass,
  exportBackup,
  importBackup,
  getQueueLength,
} from "@/lib/api";
import { STAGE_PRESETS } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Rocket as RocketIcon,
  Users,
  Trash2,
  Pencil,
  Download,
  Upload,
  Wifi,
  WifiOff,
  RefreshCw,
  DownloadCloud,
  Share2,
} from "lucide-react";

export default function ClassesList() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [preset, setPreset] = useState("Space Journey");
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pending, setPending] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const res = await fetchClasses();
    if (res.authRequired) { navigate("/"); return; }
    setClasses(res.data);
    setOffline(res.offline);
    setPending(getQueueLength());
    setLoading(false);
  };

  useEffect(() => {
    load();
    const onSync = (e) => {
      setPending(getQueueLength());
      if (e.detail?.synced > 0) {
        toast.success(`Synced ${e.detail.synced} change${e.detail.synced > 1 ? "s" : ""} ☁️`);
        load();
      }
    };
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    const onInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("starlight-sync", onSync);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstall);
    const poll = setInterval(() => setPending(getQueueLength()), 4000);
    return () => {
      window.removeEventListener("starlight-sync", onSync);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onInstall);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      if (editing) {
        await updateClass(editing.id, { name: n });
        toast.success("Class renamed!");
      } else {
        await createClass(n, STAGE_PRESETS[preset]);
        toast.success("Mission created! 🚀");
      }
      setOpen(false);
      setName("");
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save (are you offline?)");
    }
  };

  const copyClassLink = (id) => {
    navigator.clipboard?.writeText(`${window.location.origin}/class/${id}`);
    toast.success("Board link copied — open it on your own device (teacher sign-in required).");
  };

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPreset("Space Journey");
    setOpen(true);
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    try {
      await deleteClass(target.id);
      setDeleteTarget(null);
      toast.success("Mission removed");
      load();
    } catch (e) {
      setDeleteTarget(null);
      if (e?.authRequired) {
        toast.error("Your teacher session expired — please sign in again.");
        navigate("/");
      } else {
        toast.error("Could not delete this mission");
      }
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await importBackup(file);
      toast.success("Backup restored!");
      load();
    } catch {
      toast.error("Invalid backup file");
    }
    e.target.value = "";
  };

  return (
    <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden">
      <Starfield />
      <div className="relative z-10 max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 pb-32">
        {/* Header */}
        <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-3">
            <div className="rocket-float">
              <Rocket color="#00F0FF" size={56} />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-wide">
                Stellar <span className="text-cyan-300">Steps</span>
              </h1>
              <p className="text-slate-400 text-sm">Track every pupil's journey to the stars</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              data-testid="offline-status-badge"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                offline
                  ? "bg-orange-500/20 text-orange-300 border border-orange-500/40"
                  : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
              }`}
            >
              {offline ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
              {offline ? "Offline (local)" : "Synced"}
            </div>
            {pending > 0 && (
              <div
                data-testid="pending-sync-badge"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                title="Changes saved locally, waiting to sync when back online"
              >
                <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: "2s" }} />
                {pending} to sync
              </div>
            )}
            {installPrompt && (
              <Button
                data-testid="install-app-btn"
                onClick={doInstall}
                size="sm"
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-semibold"
              >
                <DownloadCloud className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Install App</span>
              </Button>
            )}
            <Button
              data-testid="export-backup-btn"
              onClick={exportBackup}
              variant="outline"
              size="sm"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]"
            >
              <Download className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Save</span>
            </Button>
            <label>
              <input
                data-testid="import-backup-input"
                type="file"
                accept="application/json"
                className="hidden"
                onChange={onImport}
              />
              <span
                data-testid="import-backup-btn"
                className="inline-flex items-center h-9 px-3 rounded-md text-sm border border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] cursor-pointer"
              >
                <Upload className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Restore</span>
              </span>
            </label>
          </div>
        </header>

        {/* Create-class dialog (controlled; triggers live in the body) */}
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setName(""); } }}>
            <DialogContent
              data-testid="create-class-modal"
              className="bg-[#141738] border-[#252B5C] text-white"
            >
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-cyan-300">
                  {editing ? "Rename Mission" : "Launch a New Mission"}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  {editing ? "Give this class a new name." : "Give your class a name to launch a new mission."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <Input
                  data-testid="class-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="e.g. Year 4 Blue"
                  className="bg-[#0E1126] border-[#252B5C] text-white min-h-[48px] text-lg"
                  autoFocus
                />
                {!editing && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-2">
                      Stage template
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {Object.keys(STAGE_PRESETS).map((k) => (
                        <button
                          key={k}
                          data-testid={`preset-${k.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={() => setPreset(k)}
                          className={`text-left p-3 rounded-xl border transition-colors ${
                            preset === k
                              ? "border-cyan-400 bg-cyan-400/10"
                              : "border-[#252B5C] bg-[#0E1126] hover:bg-[#1C204B]"
                          }`}
                        >
                          <span className="font-heading font-semibold">{k}</span>
                          <span className="block text-xs text-slate-400 mt-1">
                            {STAGE_PRESETS[k].map((s) => s.icon + " " + s.name).join("  ·  ")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Button
                  data-testid="submit-class-btn"
                  onClick={submit}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]"
                >
                  {editing ? "Save" : "Blast Off 🚀"}
                </Button>
              </div>
            </DialogContent>
        </Dialog>

        {/* Body */}
        {loading ? (
          <p className="text-slate-400">Loading missions…</p>
        ) : classes.length === 0 ? (
          <div data-testid="empty-state" className="flex flex-col items-center justify-center text-center min-h-[55vh]">
            <div className="rocket-float inline-block mb-6">
              <Rocket color="#A855F7" size={110} />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100 mb-2">No missions yet</h2>
            <p className="text-slate-400 mb-8">Create your first class to start the journey to the stars!</p>
            <Button
              data-testid="nav-add-class-btn"
              onClick={openCreate}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 min-h-[56px] text-lg font-heading font-semibold px-8 rounded-2xl shadow-[0_0_24px_rgba(236,72,153,0.4)]"
            >
              <Plus className="w-5 h-5 mr-2" /> New Class Mission
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="nav-add-class-btn"
                onClick={openCreate}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 min-h-[52px] text-base font-heading font-semibold px-6 rounded-2xl shadow-[0_0_24px_rgba(236,72,153,0.4)]"
              >
                <Plus className="w-5 h-5 mr-2" /> New Class Mission
              </Button>
              <Input
                data-testid="class-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search classes…"
                className="bg-[#141738] border-[#252B5C] text-white min-h-[52px] flex-1 min-w-[200px] max-w-sm"
              />
            </div>
            <div data-testid="class-card-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())).map((c) => {
              const done = c.pupils.filter((p) => p.stageIndex >= c.stages.length - 1).length;
              return (
                <div
                  key={c.id}
                  data-testid={`class-card-${c.id}`}
                  className="group relative bg-[#141738] hover:bg-[#1C204B] border border-[#252B5C] hover:border-cyan-400/50 rounded-3xl p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => navigate(`/class/${c.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="rocket-float">
                      <Rocket color={c.pupils[0]?.color || "#00F0FF"} variant={c.pupils[0]?.variant} size={48} />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <Button
                        data-testid={`edit-class-btn-${c.id}`}
                        size="icon" variant="ghost"
                        onClick={() => { setEditing(c); setName(c.name); setOpen(true); }}
                        className="h-8 w-8 text-slate-300 hover:text-white hover:bg-[#252B5C]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-class-btn-${c.id}`}
                        size="icon" variant="ghost"
                        onClick={() => setDeleteTarget(c)}
                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-1 truncate">{c.name}</h3>
                  {c.teacherName && (
                    <p data-testid={`card-teacher-${c.id}`} className="text-xs text-amber-300 font-heading mb-2 truncate">
                      👑 {c.teacherName}'s class
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {c.stages.map((s, i) => (
                      <span key={i} className="text-xs bg-[#0E1126] border border-[#252B5C] rounded-full px-2 py-0.5 text-slate-300">
                        {s.icon} {s.name}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm text-slate-400">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-cyan-400" /> {c.pupils.length}
                      </span>
                      {done > 0 && (
                        <span className="flex items-center gap-1.5 text-emerald-300">🏆 {done}</span>
                      )}
                      {c.code && (
                        <span className="flex items-center gap-1 text-cyan-300 font-heading tracking-wider">
                          # {c.code}
                        </span>
                      )}
                    </div>
                    <button
                      data-testid={`invite-teacher-btn-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); copyClassLink(c.id); }}
                      title="Copy teacher invite link"
                      className="flex items-center gap-1 text-slate-300 hover:text-white"
                    >
                      <Share2 className="w-4 h-4" /> Invite
                    </button>
                  </div>
                  <Button
                    data-testid={`open-class-btn-${c.id}`}
                    className="w-full mt-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 hover:from-cyan-500 hover:to-purple-500 border border-cyan-400/40 text-white font-heading font-semibold min-h-[44px]"
                    onClick={(e) => { e.stopPropagation(); navigate(`/class/${c.id}`); }}
                  >
                    <RocketIcon className="w-4 h-4 mr-2" /> Open Board
                  </Button>
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#141738] border-[#252B5C] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this mission?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              "{deleteTarget?.name}" and all its pupil progress will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#252B5C] text-white hover:bg-[#1C204B]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-class-btn"
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ClockDock accent={null} />
    </div>
  );
}
